import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IUpdateOrderEntryDetailDTO from "../../../models/DTO/IUpdateOrderEntryDetailDTO";
import INotification from "../../../models/INotification";
import IOrder from "../../../models/IOrder";
import IOrder_Item from "../../../models/IOrder_Item";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./EcommerceCheckoutView.scss";
import EcommerceCartItem from "../EcommerceCartItem/EcommerceCartItem";
import { Socket } from "socket.io-client";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import ISubmitOrderDTO from "../../../models/DTO/ISubmitOrderDTO";
import { ProcessOrderEntryItemFn } from "../../../models/OrderEntryTypes";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import OrderEntryService from "@services/OrderEntryService";
import SupplierService from "@services/SupplierService";
import { EcommerceOrderCheckoutShipTo } from "../EcommerceOrderCheckoutShipTo/EcommerceOrderCheckoutShipTo";
import { EcommerceOrderCheckoutSummary } from "../EcommerceOrderCheckoutSummary/EcommerceOrderCheckoutSummary";
import UiContext from "@contexts/UiContext";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { socket, SocketContext } from "@contexts/SocketContext";
import IGetOrderDTO from "@models/DTO/IGetOrderDTO";
import VendorContext from "@contexts/VendorContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IOnGetOrderDTO from "@models/DTO/IOnGetOrderDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { OrdersService } from "@components/orders/Orders/OrdersService";
import ViewAccessService from "@services/ViewAccessService";
import IUserTypeAccessDetail from "@models/IUserTypeAccessDetail";
import { OrderEntryApiService } from "@services/OrderEntryApiService";
import IOnUpdateOrderEntryItemDTO from "@models/DTO/IOnUpdateOrderEntryItemDTO";
import ActivityIndicator from "@components/shared/ActivityIndicator/ActivityIndicator";
import IOnDeleteOrderEntryItemDTO from "@models/DTO/IOnDeleteOrderEntryItemDTO";
import IOnUpdateOrderEntryDetailDTO from "@models/DTO/IOnUpdateOrderEntryDetailDTO";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import OwDateInput from "@components/shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import moment from "moment";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import axios from "axios";
import SocketService from "@services/SocketService";
import ky from "ky";
import IOnSubmitOrderDTO from "@models/DTO/IOnSubmitOrderDTO";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import { hasEditAccess } from "@services/ScreenOptionsService";

type Props = {};

export const EcommerceCheckoutView = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const orderEntryApiService = OrderEntryApiService();

    const navigate = useNavigate();
    const location = useLocation();
    const match = useMatch("/:entityType/:order_guid/ecom/checkout");

    const [order, setOrder] = useState<IOrder | null>(null);
    const [selectedOrderItem, setSelectedOrderItem] = useState<IOrder_Item | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [targetElem, setTargetElem] = useState<string | null>(null);
    const [isActive, setIsActive] = useState(false);
    const [poNo, setPoNo] = useState("");
    const [note, setNote] = useState("");
    const [doNotShipBeforeDate, setDoNotShipBeforeDate] = useState<Moment | null>(null);
    const [cancelDate, setCancelDate] = useState<Moment | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const summaryTitleName = useMemo(() => {
        return order?.is_template ? "template_summary" : order?.is_quote ? "quote_summary" : "order_summary";
    }, [order]);

    const isAddressValid = useMemo(() => {
        return SupplierService.isAddressValidForCheckout(supplierModel, order?.Ship_To);
    }, [order?.Ship_To, supplierModel]);

    useEffect(() => {
        // Initialize po_no and note from order when order changes
        if (order) {
            setPoNo(order.po_no || "");
            setNote(order.note || "");
            // Parse UTC dates as local dates to preserve the date value
            // This treats "2025-06-11T00:00:00.000Z" as "2025-06-11" in local time
            setDoNotShipBeforeDate(order.do_not_ship_before_date ? moment.utc(order.do_not_ship_before_date) : null);
            setCancelDate(order.cancel_date ? moment.utc(order.cancel_date) : null);
        }
    }, [order]);

    useEffect(() => {
        // Check if there are unsaved changes
        if (order) {
            const originalPoNo = order.po_no || "";
            const originalNote = order.note || "";
            const originalDoNotShipBeforeDate = order.do_not_ship_before_date
                ? moment.utc(order.do_not_ship_before_date).format("YYYY-MM-DD")
                : null;
            const originalCancelDate = order.cancel_date ? moment.utc(order.cancel_date).format("YYYY-MM-DD") : null;

            const doNotShipBeforeDateString = doNotShipBeforeDate ? doNotShipBeforeDate.format("YYYY-MM-DD") : null;
            const cancelDateString = cancelDate ? cancelDate.format("YYYY-MM-DD") : null;

            const hasChanges =
                poNo !== originalPoNo ||
                note !== originalNote ||
                doNotShipBeforeDateString !== originalDoNotShipBeforeDate ||
                cancelDateString !== originalCancelDate;
            setHasUnsavedChanges(hasChanges);
        }
    }, [poNo, note, order, doNotShipBeforeDate, cancelDate]);

    let orderStateName = order?.Order_State?.name.toLowerCase() || "";

    if (orderStateName === "in_product") {
        orderStateName = "confirmed";
    }

    const allowSubmit = (): boolean => {
        if (order) {
            let hasEditMode = true;

            if (userModel.userType) {
                hasEditMode = hasEditAccess(ScreenOptionsEnum.ORDER_ENTRY, userModel, supplierModel, ViewAccessService.OrderEntry);
            }

            const result = OrderEntryService.allowSubmit(supplierModel, userModel, order, hasEditMode);
            return result.allowSubmit;
        } else {
            return false;
        }
    };

    const isReadOnly = useCallback(() => {
        if (order) {
            const hasEditMode = hasEditAccess(ScreenOptionsEnum.ORDER_ENTRY, userModel, supplierModel, ViewAccessService.OrderEntry);

            return OrdersService.isReadOnly({
                order,
                isOfflineOrder: false,
                hasEditMode,
                supplierModel: supplierModel,
                userModel: userModel,
            });
        }

        return false;
    }, [order]);

    const deleteOrderEntryItem = useCallback(
        (item: IOrder_Item) => {
            if (order) {
                orderEntryApiService.deleteOrderEntryItem(order, item);
                uiContext.setActivityIndicator(true);
            }
        },
        [order]
    );

    const updateOrderEntryItem: ProcessOrderEntryItemFn = useCallback(
        (rowData, options) => {
            if (order) {
                orderEntryApiService.updateOrderEntryItem(order, rowData, options);

                uiContext.setActivityIndicator(true);
            }
        },
        [order]
    );

    const updateOrderDetails = useCallback(
        (data: Partial<IUpdateOrderEntryDetailDTO>) => {
            if (order) {
                orderEntryApiService.updateOrderEntryDetails(order, data);
                uiContext.setActivityIndicator(true);
            }
        },
        [order]
    );

    const onSubmitOrderFn = (data: IOnSubmitOrderDTO) => {
        setIsActive(false);

        if (supplierModel.redirect_on_submit) {
            const notification: INotification = {
                message: "successfully_advanced_order",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.success,
            };

            uiContext.notifyFn(null, notification);

            navigate("/orders");
        } else {
            getOrder();
        }
    };

    const submitOrderFn = () => {
        if (order) {
            const { currentStep, nextStep } = OrderEntryService.getCurrentAndNextOrderSteps(order, supplierModel);

            if (nextStep) {
                const payload: ISubmitOrderDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    orderGuid: order.guid,
                };

                if (!userModel.isSupplierUser) {
                    payload.vendorGuid = order.Vendor!.guid;
                }

                const url = SocketService.getEndpointUrl() + "/v1/order/submit";
                const headers: HeadersInit = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                };

                if (!userModel.isSupplierUser) {
                    headers!.vendorGuid = vendorModel.guid;
                }

                setIsActive(true);

                ky.post(url, {
                    json: payload,
                    headers,
                })
                    .then(async (res) => {
                        // console.log("Submit Order Response", res.data);
                        const data = await res.json<IOnSubmitOrderDTO>();
                        onSubmitOrderFn(data);
                    })
                    .catch((err) => {
                        const notification: INotification = {
                            message: "error_submitting_order",
                            level: NotificationLevelEnum.error,
                            position: NotificationPositionEnum.bc,
                        };

                        uiContext.notifyFn(null, notification);

                        setIsActive(false);
                    });
            }
        }
    };

    const getOrder = useCallback(() => {
        if (match?.params.order_guid) {
            const payload: IGetOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                orderGuid: match.params.order_guid,
                supplierGuid: supplierModel.guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.OrderGetOrder, payload);
            uiContext.setActivityIndicator(true);
        }
    }, [match]);

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);
        uiContext.setActivityIndicator(false);
    };

    const onUpdateOrderEntryItem = (data: IOnUpdateOrderEntryItemDTO) => {
        getOrder();
    };

    const onDeleteOrderEntryItem = (data: IOnDeleteOrderEntryItemDTO) => {
        getOrder();
    };

    const onUpdateOrderEntryDetail = (data: IOnUpdateOrderEntryDetailDTO) => {
        const notification: INotification = {
            message: "successfully_updated_details",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        getOrder();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
        { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryItem, fn: onUpdateOrderEntryItem },
        { endpoint: EndpointsEnum.OrderEntryOnDeleteOrderEntryItem, fn: onDeleteOrderEntryItem },
        { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryDetail, fn: onUpdateOrderEntryDetail },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        uiContext.setActivityIndicator(true);

        uiContext.setSubHeaderComponent(null);

        getOrder();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const toggleConfirmDelete = () => {
        setShowConfirmDelete(!showConfirmDelete);
    };

    const commitDelete = () => {
        if (selectedOrderItem) {
            deleteOrderEntryItem(selectedOrderItem);
            setShowConfirmDelete(false);
            setSelectedOrderItem(null);
        }
    };

    const selectElementToDelete = (row: IOrder_Item, index: number) => {
        setSelectedOrderItem(row);

        const elem = `order-item-row-${index}`;
        setTargetElem(elem);
        setShowConfirmDelete(true);
    };

    const saveOrderDetails = () => {
        if (order && hasUnsavedChanges) {
            const doNotShipBeforeDateString = doNotShipBeforeDate ? doNotShipBeforeDate.format("YYYY-MM-DD") : null;
            const cancelDateString = cancelDate ? cancelDate.format("YYYY-MM-DD") : null;
            updateOrderDetails({
                po_no: poNo,
                note: note,
                do_not_ship_before_date: doNotShipBeforeDateString,
                cancel_date: cancelDateString,
            });
            setHasUnsavedChanges(false);
        }
    };

    const returnToEcommerceView = () => {
        if (order?.is_template) {
            navigate(`/templates/${order!.guid}/ecom`);
        } else if (order?.is_quote) {
            navigate(`/quotes/${order!.guid}/ecom`);
        } else {
            navigate(`/orders/${order!.guid}/ecom`);
        }
    };

    if (!order) {
        return (
            <div className={"EcommerceCheckoutView"}>
                <div className={"mt-4 mb-4 text-center d-flex justify-content-center align-items-center"}>
                    <h4>
                        <ActivityIndicator active={true} className="me-2" />
                    </h4>
                    <OwTitle title={"loading"} tag={"h3"} leftIconName={"fa-rotate fas-refresh"} />
                </div>
            </div>
        );
    }

    return (
        <div className={"EcommerceCheckoutView"}>
            <div className={"back mt-4"}>
                <div>
                    <OwButton title={"back"} leftIconClass={"fas fa-arrow-left"} fn={returnToEcommerceView} />
                </div>
            </div>
            <div className={"mt-4 mb-4 text-center"}>
                {!isReadOnly() && (
                    <OwTitle
                        title={order.is_template ? "review_template" : order.is_quote ? "review_quote" : "review_and_submit"}
                        tag={"h5"}
                    />
                )}
                <OwTitle title={order.name || ""} disableTranslation={true} tag={"h2"} />
            </div>

            <div className="row">
                <div className="col-md-9">
                    <div>
                        <EcommerceOrderCheckoutShipTo order={order} isReadOnly={isReadOnly() || !allowSubmit()} />
                    </div>

                    <div id="checkout-items" className={"mt-5 mb-5 position-relative"}>
                        {showConfirmDelete && (
                            <ConfirmDeleteOverlayFn
                                targetElem={targetElem}
                                handleConfirm={commitDelete}
                                handleCancel={toggleConfirmDelete}
                                nestedParentElem="checkout-items"
                                skipMainView={true}
                                className="rounded"
                            />
                        )}

                        <OwTitle title={"2_review_items"} tag={"h5"} isUnderline={true} />
                        {!order.Order_Items.length && (
                            <div className="no-results">
                                <OwLabel title={"no_items_added"} />
                            </div>
                        )}
                        {order.Order_Items.map((orderItem, index) => {
                            return (
                                <div key={orderItem.guid} className="mb-2">
                                    <EcommerceCartItem
                                        orderItem={orderItem}
                                        order={order}
                                        userModel={userModel}
                                        supplierModel={supplierModel}
                                        socket={socket}
                                        notifyFn={uiContext.notifyFn}
                                        selectElementToDelete={selectElementToDelete}
                                        // deleteOrderEntryItem={props.deleteOrderEntryItem}
                                        updateOrderEntryItem={updateOrderEntryItem}
                                        isReadOnly={isReadOnly()}
                                        isInSidebar={false}
                                        index={index}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="order-summary col-md-3">
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-transparent border-0 text-center py-3">
                            <OwTitle className="card-title mb-0 text-uppercase fw-semibold" title={summaryTitleName} tag={"h6"} />
                        </div>
                        <div className="card-body p-4">
                            {!isReadOnly() && allowSubmit() && !order.is_template && !order.is_quote && (
                                <>
                                    {!isAddressValid && (
                                        <div className="alert alert-warning mb-3 text-center">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            <OwLabel title={"address_required_for_checkout"} />
                                        </div>
                                    )}
                                    <div className="d-grid gap-2 mb-4">
                                        <OwButton
                                            className="btn-warning btn-lg fw-semibold py-3"
                                            title={"submit_order"}
                                            fn={submitOrderFn}
                                            active={isActive}
                                            disableOnActive={true}
                                            disabled={order?.Order_Items?.length === 0 || !isAddressValid}
                                        />
                                    </div>
                                </>
                            )}
                            <div>
                                <EcommerceOrderCheckoutSummary order={order} />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="mb-3">
                            <div>
                                <OwLabel title={"po_no"} className="me-2" postText={":"} isBold={true} />
                            </div>
                            <div>
                                <OwTextArea
                                    name={"po_no"}
                                    value={poNo}
                                    fn={(e) => setPoNo(e.target.value)}
                                    disabled={isReadOnly() || !allowSubmit()}
                                />
                            </div>
                        </div>

                        <div className="mb-3">
                            <div>
                                <OwLabel title={"note"} className="me-2" postText={":"} isBold={true} />
                            </div>
                            <div>
                                <OwTextArea
                                    name={"note"}
                                    value={note}
                                    fn={(e) => setNote(e.target.value)}
                                    disabled={isReadOnly() || !allowSubmit()}
                                />
                            </div>
                        </div>

                        {supplierModel.has_order_cancel_date && (
                            <div className="mb-3">
                                <div>
                                    <OwLabel title={"cancel_date"} className="me-2" postText={":"} isBold={true} />
                                </div>
                                <div>
                                    <OwDateInput
                                        selectedDate={cancelDate}
                                        fn={(date) => setCancelDate(date)}
                                        isUTC={true}
                                        disabled={isReadOnly() || !allowSubmit()}
                                    />
                                </div>
                            </div>
                        )}

                        {supplierModel.has_do_not_ship_before_date && (
                            <div className="mb-3">
                                <div>
                                    <OwLabel title={"do_not_ship_before_date"} className="me-2" postText={":"} isBold={true} />
                                </div>
                                <div>
                                    <OwDateInput
                                        selectedDate={doNotShipBeforeDate}
                                        fn={(date) => setDoNotShipBeforeDate(date)}
                                        isUTC={true}
                                        disabled={isReadOnly() || !allowSubmit()}
                                    />
                                </div>
                            </div>
                        )}

                        {hasUnsavedChanges && (
                            <div className="mb-3">
                                <OwButton className="btn-primary" title="save_changes" fn={saveOrderDetails} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
